import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import agent from "../../../agent";
import BlockDialog, { SCOPES } from "./BlockDialog";
import { mileLabel } from "../TariffPlans/mileTypes";

/**
 * Закрытые направления.
 *
 * Закрытый вектор не считается вовсе: клиент получает причину, а не цену и не
 * пустой список маршрутов. Отката на стандартный тариф у блокировки нет — иначе
 * она удешевляла бы направление вместо того, чтобы его закрыть.
 *
 * Блокировки тарифной карты приезжают заливкой файла (колонка «блокировать
 * направление»); повторная заливка их замещает, поэтому правки руками в строках
 * карты живут только до следующей заливки. Глобальные и партнёрские правила
 * заливка не трогает.
 */
const BlockedDirections = () => {
  const toast = React.useRef(null);

  const [rows, setRows] = React.useState([]);
  const [plans, setPlans] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [dialogVisible, setDialogVisible] = React.useState(false);
  const [editBlock, setEditBlock] = React.useState(null);

  const showError = (detail) => toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
  const showSuccess = (detail) => toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });

  React.useEffect(() => {
    agent.TariffPlans.list()
      .then((res) => setPlans(Array.isArray(res) ? res : []))
      .catch(() => {});
    agent.OrgTariffs.postalServices()
      .then((options) =>
        setServices(
          (options ?? []).map((option) => ({
            ...option,
            label: `${option.psCode} — ${option.psTypeCode}${option.name ? ` (${option.name})` : ""}`,
          }))
        )
      )
      .catch(() => {});
  }, []);

  const load = React.useCallback(() => {
    setLoading(true);
    agent.BlockedDirections.list()
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => showError(readError(err)))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onSave = (data) => {
    setSaving(true);
    const request = editBlock
      ? agent.BlockedDirections.update(editBlock.id, data)
      : agent.BlockedDirections.create(data);

    request
      .then(() => {
        showSuccess(editBlock ? "Блокировка сохранена" : "Направление закрыто");
        setDialogVisible(false);
        setEditBlock(null);
        load();
      })
      .catch((err) => showError(readError(err)))
      .finally(() => setSaving(false));
  };

  /** Тумблер открывает направление, не удаляя правило: сезонные закрытия повторяются. */
  const onToggleActive = (row, isActive) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive } : r)));
    agent.BlockedDirections.update(row.id, { ...row, isActive })
      .then(() => showSuccess(isActive ? "Направление закрыто" : "Направление открыто"))
      .catch((err) => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: !isActive } : r)));
        showError(readError(err));
      });
  };

  const onDelete = (row) => {
    confirmDialog({
      header: "Удалить блокировку?",
      message: `${row.originKato} → ${row.destinationKato}: направление откроется сразу.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Удалить",
      rejectLabel: "Отмена",
      accept: () =>
        agent.BlockedDirections.remove(row.id)
          .then(() => { showSuccess("Блокировка удалена"); load(); })
          .catch((err) => showError(readError(err))),
    });
  };

  const scopeBody = (row) => {
    const label = SCOPES.find((s) => s.value === row.scope)?.label ?? row.scope;
    if (row.scope === "PLAN") return `${label}: ${row.tariffPlanName ?? row.tariffPlanId}`;
    if (row.scope === "PARTNER") return `${label}: ${row.platform}${row.clientName ? ` / ${row.clientName}` : ""}`;
    return label;
  };

  const actionsBody = (row) => (
    <div style={{ display: "flex", gap: 4 }}>
      <Button icon="pi pi-pencil" className="p-button-rounded p-button-text"
              onClick={() => { setEditBlock(row); setDialogVisible(true); }} />
      <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger"
              onClick={() => onDelete(row)} />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="p-d-flex p-jc-between p-ai-center">
        <h1>Закрытые направления</h1>
        <Button label="Закрыть направление" icon="pi pi-ban"
                onClick={() => { setEditBlock(null); setDialogVisible(true); }} />
      </div>

      <Message severity="warn" style={{ display: "block", marginBottom: 10 }}
               text="По закрытому направлению расчёт не выполняется: клиент видит причину, а не цену. Отката на стандартный тариф нет. Блокировки тарифных карт приезжают заливкой файла и замещаются при следующей заливке." />

      <DataTable value={rows} loading={loading} dataKey="id" emptyMessage="Закрытых направлений нет">
        <Column header="Направление" style={{ width: "220px" }}
                body={(row) => `${row.originKato} → ${row.destinationKato}`} />
        <Column header="Кого закрывает" body={scopeBody} />
        <Column header="Плечо" body={(row) => (row.mileType ? mileLabel(row.mileType) : "любое")} style={{ width: "140px" }} />
        <Column header="Услуга" body={(row) => serviceLabel(services, row.psId)} style={{ width: "200px" }} />
        <Column field="reason" header="Причина" body={(row) => row.reason || <span style={{ color: "#6b7280" }}>общий текст</span>} />
        <Column header="Закрыто" style={{ width: "110px" }}
                body={(row) => <InputSwitch checked={!!row.isActive} onChange={(e) => onToggleActive(row, e.value)} />} />
        <Column header="" body={actionsBody} style={{ width: "110px" }} />
      </DataTable>

      <BlockDialog visible={dialogVisible} loading={saving} block={editBlock} plans={plans} services={services}
                   onHide={() => { setDialogVisible(false); setEditBlock(null); }}
                   onSubmit={onSave} />
    </div>
  );
};

const serviceLabel = (services, psId) =>
  psId == null ? "любая" : (services.find((s) => s.psId === psId)?.label ?? psId);

const readError = (err) =>
  (typeof err === "string" ? err : null)
  || err?.response?.body?.messages?.[0]
  || err?.response?.body?.message
  || agent.handleError(err);

export default BlockedDirections;
